import { MarkdownText } from '../markdown-text';
import { MarkdownHint } from '../markdown-hint';
import Config from "../../general/config";
import React, { useEffect, useState } from "react";
import { Dialog } from '../dialogs/dialog';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { UserAnswerVerificationResult } from "./rendering-helpers";
import { AiValidationResponseDataDto, FormFieldDto } from "../../general/dtos";
import { fieldDisplayNames, FieldDisplayName } from './field-display-names';
import { GeneralInfoDialog } from "../dialogs/info-dialog";
import { hasExpectedAnswer, isMultiChoiceType, selectionScore, expectsBlankInline } from './multi-choice-fields';
import { readableAnswer } from './fragment-answers';
import { computeVerificationSummary, normalizeAiChoiceResults, verificationScore } from './form-ai-validation';
import { fieldWeight } from '../../general/field-weight';
import { quizItemLabel, quizTargetDisplays, QuizTargetDisplay } from './quiz-targets';

interface FormVerificationDialogProps {
  visible: boolean;
  results: UserAnswerVerificationResult[];
  showUserResults: boolean;
  answersExplanation?: string | null;
  onClose: () => void;
  parent?: HTMLDivElement | null;
  isAiVerifying?: boolean;
  onVerifyWithAi?: () => void;
  aiValidationResult?: AiValidationResponseDataDto | null;
  isAiVerificationAvailable?: boolean;
  showAiColumn?: boolean;
  fragmentHtml?: string;
  formFields?: FormFieldDto[];
}

interface AnswerTableRow {
  fieldKey: string;
  question: string;
  questionContext?: FieldDisplayName['context'];
  isUnnamedInlineField: boolean;
  userAnswer: string;
  expectedAnswer: string;
  icon: "correct" | "partial" | "wrong" | "notchecked";
  score: number | null;
  weight: number;
  tooltip: string;
}

function buildAnswerTableRows(
  results: UserAnswerVerificationResult[],
  aiValidationResult?: AiValidationResponseDataDto | null,
  names: Record<string, FieldDisplayName> = {},
  quizTargets: QuizTargetDisplay[] = []
): AnswerTableRow[] {
  const aiResults = new Map((aiValidationResult?.fieldResults ?? []).map(result => [result.fieldKey, result]));
  const quizCounts = new Map<string, number>();
  return results?.map(r => {
    const isQuiz = r.field.type === 'quiz-list';
    const quizIndex = (quizCounts.get(r.field.key) ?? 0) + 1;
    if (isQuiz) quizCounts.set(r.field.key, quizIndex);
    const hasExpected = hasExpectedAnswer(r.expectedAnswer, r.field.type);
    const expectsBlank = expectsBlankInline(r.field.type, r.expectedAnswer);
    const choiceScore = isMultiChoiceType(r.field.type) ? selectionScore(r.userAnswer, r.expectedAnswer) : null;
    const verdict = choiceScore !== null ? (choiceScore === 1 ? 'correct' : choiceScore === 0 ? 'incorrect' : 'partial')
        : aiResults.get(r.field.key)?.verdict;
    const normalizedScore = verificationScore(r, aiResults.get(r.field.key));

    // Resolve display values for Quiz types
    let displayUser = readableAnswer(r.field, r.userAnswer) || (Array.isArray(r.userAnswer) ? 'None' : '');
    let displayExpected = readableAnswer(r.field, r.expectedAnswer) || (Array.isArray(r.expectedAnswer) ? 'None' : '');
    if (expectsBlank) {
      displayExpected = 'Leave blank';
      if (typeof r.userAnswer === 'string' && !r.userAnswer.trim()) displayUser = 'Left blank';
    }

    if (isQuiz) {
      const options = r.field.options ?? [];
      const userOpt = options.find(o => o.key === r.userAnswer);
      const expectedOpt = options.find(o => o.key === r.expectedAnswer);
      
      displayUser = userOpt ? userOpt.value : (r.userAnswer ? "Unknown" : "None");
      displayExpected = expectedOpt ? expectedOpt.value : "N/A";
    }

    return {
      fieldKey: r.field.key,
      question: isQuiz ? quizTargets.find(target => target.fieldKey === r.field.key && target.expectedKey === r.expectedAnswer)?.label
        ?? quizItemLabel(r.field, quizIndex) : (names[r.field.key]?.text ?? r.field.label).replace(/:$/, "").trim(),
      questionContext: names[r.field.key]?.context,
      isUnnamedInlineField: r.field.type === 'inline-text' && !r.field.label.trim(),
      userAnswer: displayUser,
      expectedAnswer: hasExpected ? displayExpected : "N/A",
      icon: verdict === 'correct' ? 'correct' : verdict === 'partial' ? 'partial' : verdict === 'incorrect' ? 'wrong'
        : !hasExpected ? 'notchecked' : r.isCorrect ? 'correct' : 'wrong',
      score: normalizedScore === null ? null : normalizedScore * fieldWeight(r.field),
      weight: fieldWeight(r.field),
      tooltip: r.field.tooltip ?? ''
    };
  });
}

function resultTooltip(row: AnswerTableRow): string {
    const label = row.icon === 'correct' ? 'Correct' : row.icon === 'wrong' ? 'Incorrect'
        : row.icon === 'partial' ? 'Partially correct' : 'Not checked';
    return row.score === null ? label : `${label} (score ${row.score.toFixed(2)} out of ${row.weight})`;
}

export const FormVerificationDialog: React.FC<FormVerificationDialogProps> = ({
  visible,
  results,
  onClose,
  parent,
  showUserResults,
  answersExplanation,
  isAiVerifying,
  onVerifyWithAi,
  aiValidationResult,
  isAiVerificationAvailable,
  showAiColumn = false,
  fragmentHtml,
  formFields
}) => {
  const [isMobileLayout, setIsMobileLayout] = useState<boolean>(window.innerWidth <= 640);
  const [selectedAiFeedback, setSelectedAiFeedback] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobileLayout(window.innerWidth <= 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fields = formFields ?? results?.map(result => result.field) ?? [];
  const rows = buildAnswerTableRows(results, aiValidationResult, fieldDisplayNames(fields, fragmentHtml), quizTargetDisplays(fields, fragmentHtml));
  const summary = computeVerificationSummary(results, aiValidationResult);
  const aiFieldResultMap = new Map(
    (aiValidationResult ? normalizeAiChoiceResults(results, aiValidationResult).fieldResults : []).map(fieldResult => [
        fieldResult.fieldKey,
        fieldResult
    ])
  );

  const aiVerdictTemplate = (row: AnswerTableRow) => {
    const aiFieldResult = aiFieldResultMap.get(row.fieldKey);
    if (!aiFieldResult) {
        return null;
    }

    const verdictColor: string = aiFieldResult.verdict === "correct" ? "green" : aiFieldResult.verdict === "partial" ? "#d97706" : "red";
    return (<>
        <span
            onClick={() => setSelectedAiFeedback(aiFieldResult.feedback)}
            style={{ cursor: "pointer", color: verdictColor, fontSize: "1.1rem" }}
            title={aiFieldResult.verdict === 'partial' ? `Partial credit: ${row.score?.toFixed(2)} out of ${row.weight}. Click to view AI feedback` : 'Click to view AI feedback'}
        >●</span>
        <span style={{ color: "var(--text-color-secondary)", fontSize: "0.9rem", lineHeight: 1 }}>🤖</span>
    </>);
  };

  const iconTemplate = (row: AnswerTableRow) => {
    if (row.icon === 'partial') {
        return <i className="pi pi-minus-circle" aria-label="Partially correct" title={resultTooltip(row)}
            style={{ color: '#eab308', fontSize: '1.2rem' }} />;
    }
    if (row.icon === "notchecked") {
        return <span aria-label="Not checked" style={{ color: "gray", fontSize: "1.2rem", fontStyle: "italic" }}>—</span>;
    }
    
    return row.icon === "correct" ? (
        <span aria-label="Correct" title={resultTooltip(row)} style={{ color: "green", fontSize: "1.2rem" }}>✔</span>
    ) : (
        <span aria-label="Incorrect" title={resultTooltip(row)} style={{ color: "red", fontSize: "1.2rem" }}>✘</span>
    );
  };

  const questionTemplate = (row: AnswerTableRow) => {
    const highlightedBlank = (text: string) => <strong style={{ color: 'var(--primary-color, #2563eb)', fontWeight: 700 }}>{text}</strong>;
    return row.questionContext ? <>{row.questionContext.before}{highlightedBlank(row.questionContext.blank)}{row.questionContext.after}</>
        : row.isUnnamedInlineField ? highlightedBlank(row.question) : <MarkdownText text={row.question} inline />;
  };

  const tooltipTemplate = (rowData: AnswerTableRow, column: any) => {
    const field = column.field as 'question' | 'userAnswer' | 'expectedAnswer';
    const value = rowData[field];

    return (
        <MarkdownHint content={rowData.tooltip} style={{ display: 'block', width: '100%', cursor: rowData.tooltip ? 'help' : 'default' }}>
            {field === 'question' ? questionTemplate(rowData) : value}
        </MarkdownHint>
    );
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
        {Config.showAiControls && !!onVerifyWithAi && (
        <Button
            label={ isAiVerifying ? "Verifying..." : isAiVerificationAvailable === false ? "AI verified" : "Verify with AI" }
            icon="pi pi-sparkles"
            onClick={onVerifyWithAi}
            disabled={isAiVerifying || !results?.length || isAiVerificationAvailable === false}
            className="p-button-outlined"
        />
        )}
        <Button label="Close" icon="pi pi-times" onClick={onClose} />
    </div>
  );

  const mobileResultCard = (row: AnswerTableRow, index: number) => {
    const resultLabel =
        row.icon === "correct" ? "Correct" :
        row.icon === "partial" ? "Partially correct" :
        row.icon === "wrong" ? "Incorrect" :
        "Not checked";

    const resultColor =
        row.icon === "correct" ? "green" :
        row.icon === "partial" ? "#eab308" :
        row.icon === "wrong" ? "red" :
        "gray";

    return (
        <div
        key={index}
        style={{
            border: "1px solid var(--surface-border)",
            borderRadius: "12px",
            padding: "1rem",
            marginBottom: "0.75rem",
            background: "var(--surface-card)"
        }}
        >
        <div style={{ marginBottom: "0.65rem" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginBottom: "0.2rem" }}>
            Question
            </div>
            <div><MarkdownHint content={row.tooltip}>{questionTemplate(row)}</MarkdownHint></div>
        </div>

        {showUserResults && (
            <div style={{ marginBottom: "0.65rem" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginBottom: "0.2rem" }}>
                Your answer
            </div>
            <div><MarkdownHint content={row.tooltip}>{row.userAnswer}</MarkdownHint></div>
            </div>
        )}

        <div style={{ marginBottom: "0.65rem" }}>
            <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginBottom: "0.2rem" }}>
            {showUserResults ? "Correct answer" : "Answer"}
            </div>
            <div><MarkdownHint content={row.tooltip}>{row.expectedAnswer}</MarkdownHint></div>
        </div>

        {showUserResults && (
            <div>
            <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginBottom: "0.2rem" }}>
                Result
            </div>
            <div title={row.icon === 'notchecked' ? undefined : resultTooltip(row)} style={{ color: resultColor, fontWeight: 600 }}>
                {iconTemplate(row)} {resultLabel}
            </div>
            </div>
        )}

        {showUserResults && showAiColumn && (() => {
            const aiFieldResult = aiFieldResultMap.get(row.fieldKey);

            if (!aiFieldResult) {
                return null;
            }

            const verdictLabel =
                aiFieldResult.verdict === "correct"
                ? "Correct"
                : aiFieldResult.verdict === "partial"
                ? "Partially correct"
                : "Incorrect";

            const verdictColor =
                aiFieldResult.verdict === "correct"
                ? "green"
                : aiFieldResult.verdict === "partial"
                ? "#d97706"
                : "red";

            return (
                <div style={{ marginTop: "0.65rem" }}>
                <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginBottom: "0.2rem" }}>
                    AI verification
                </div>
                <div
                    style={{ color: verdictColor, fontWeight: 600, cursor: "pointer" }}
                    onClick={() => setSelectedAiFeedback(aiFieldResult.feedback)} >
                    {verdictLabel}{aiFieldResult.verdict === 'partial' ? ` (${row.weight === 1 ? row.score : `${row.score?.toFixed(2)} out of ${row.weight}`})` : ''}
                    <span style={{ color: "var(--text-color-secondary)", fontSize: "0.9rem", lineHeight: 1 }}>🤖</span>
                </div>
                </div>
            );
            })()}
        </div>
    );
  };

  return (
    <Dialog
      header={showUserResults ? "Your results" : "Correct answers"}
      visible={visible}
      style={{ width: isMobileLayout ? "95vw" : "50vw", maxWidth: "900px" }}
      modal
      appendTo={parent}
      onHide={onClose}
      footer={footer}
    >
      {showUserResults && isAiVerifying && <div role="status" aria-live="polite"
        className="flex align-items-center gap-2 mb-3 p-3 surface-ground border-round" style={{ fontWeight: 'bold' }}>
        <i className="pi pi-spinner pi-spin" aria-hidden="true" />
        <span>Verifying answers...</span>
      </div>}
      {!(showUserResults && isAiVerifying) && <>
      {isMobileLayout ? (
        <div>
            {rows?.map((row, index) => mobileResultCard(row, index))}
        </div>
        ) : (
        <DataTable
            stripedRows
            value={rows}
            responsiveLayout="scroll"
            showGridlines
        >
            <Column
                field="question"
                body={tooltipTemplate}
                header="Question"
                headerStyle={{ borderBottom: '3px solid var(--primary-color)', fontSize: '1.2rem', paddingBottom: '10px' }}
            />
            <Column
                field="userAnswer"
                body={tooltipTemplate}
                header="Your answer"
                hidden={!showUserResults}
                headerStyle={{ borderBottom: '3px solid var(--primary-color)', fontSize: '1.2rem', paddingBottom: '10px' }}
            />
            <Column
                field="expectedAnswer"
                body={tooltipTemplate}
                header={showUserResults ? "Correct answer" : "Answer"}
                headerStyle={{ borderBottom: '3px solid var(--primary-color)', fontSize: '1.2rem', paddingBottom: '10px' }}
            />
            <Column
                header="Result"
                body={iconTemplate}
                style={{ width: "6rem", textAlign: "center" }}
                hidden={!showUserResults}
                headerStyle={{ borderBottom: '3px solid var(--primary-color)', fontSize: '1.2rem', paddingBottom: '10px' }}
            />
            <Column
                header="AI"
                body={aiVerdictTemplate}
                style={{ width: "4rem", textAlign: "center" }}
                hidden={!showUserResults || !showAiColumn}
                headerStyle={{ borderBottom: '3px solid var(--primary-color)', fontSize: '1.2rem', paddingBottom: '10px' }}
            />
        </DataTable>
        )}

      {/* Summary message */}
      <div style={{
            marginTop: "1rem",
            fontWeight: "bold",
            fontSize: isMobileLayout ? "0.95rem" : "1rem",
            lineHeight: 1.5,
            visibility: showUserResults ? "visible" : "hidden"
        }}>
        {!isAiVerifying && <>
          You scored {summary.correct} out of a maximum of {summary.total}
          ({summary.percentage}%).
        </>}
      </div>
      {showUserResults && answersExplanation?.trim() && (
        <section className="mt-3 p-3 surface-ground border-round" aria-label="Answers explanation">
          <h3 className="mt-0 mb-2">Answers explanation</h3>
          <MarkdownText text={answersExplanation} />
        </section>
      )}

      {showUserResults && showAiColumn && aiValidationResult?.summary && (
        <div
            style={{
            marginTop: "1rem",
            padding: "1rem",
            border: "1px solid var(--surface-border)",
            borderRadius: "12px",
            background: "var(--surface-card)"
            }}
        >
            <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
            AI summary
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
            {aiValidationResult.summary.overallImpression}
            </div>

            {aiValidationResult.summary.strengths?.length > 0 && (
            <div style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Strengths</div>
                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {aiValidationResult.summary.strengths.map((item, index) => (
                    <li key={`strength-${index}`}>{item}</li>
                ))}
                </ul>
            </div>
            )}

            {aiValidationResult.summary.priorities?.length > 0 && (
            <div>
                <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Priorities</div>
                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {aiValidationResult.summary.priorities.map((item, index) => (
                    <li key={`priority-${index}`}>{item}</li>
                ))}
                </ul>
            </div>
            )}
        </div>
        )}

      </>}

        <GeneralInfoDialog
            parent={parent}
            visible={selectedAiFeedback !== null}
            title="AI feedback"
            message={selectedAiFeedback!}
            onClose={() => setSelectedAiFeedback(null)}
        ><MarkdownText text={selectedAiFeedback} /></GeneralInfoDialog>
    </Dialog>
  );
};
